/**
 * HUD Context
 *
 * Provides shared state for the Orders Command Center including:
 * - Threat detection (SLA breach prediction)
 * - Priority scanning (VIP detection)
 * - Operational insights (pattern recognition)
 * - Sound state
 * - Filter state
 */

import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ThreatLevel, PriorityBadge } from '../theme';
import { getOrderStats, subscribeOrderStats } from '../orderAnalyticsStore';

// =============================================================================
// Types
// =============================================================================

export interface Order {
  id: string;
  number: string;
  status: string;
  total: number;
  // Money breakdown — all returned by the orders API, used by the preview drawer.
  totalCart?: number | string;
  totalDeliveryFee?: number | string;
  totalServiceFee?: number | string;
  totalDriverTip?: number | string;
  totalPromo?: number | string;
  /** Gateway used (ozow, paygate, snapscan, zapper, payflex, payjustnow). */
  paymentMethod?: string | null;
  isPaid?: boolean;
  /** Customer's delivery note. */
  shippingNote?: string | null;
  refundStatus?: string;
  createdAt: string;
  pickingStartedAt?: string;
  packedAt?: string;
  dispatchedAt?: string;
  deliveredAt?: string;
  storeId: string;
  userId: string;
  driverName?: string;
  driverPhotoUrl?: string;
  driverPhone?: string;
  coupons?: any[];
  user?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    totalOrders?: number;
  };
  store?: {
    name?: string;
    alertSettings?: {
      pickingSlaMinutes?: number;
      packingSlaMinutes?: number;
      pickupSlaMinutes?: number;
    };
  };
  lines?: any[];
  // Server-enriched fields (from backend order-analytics.js)
  threat?: ThreatInfo | null;
  priority?: PriorityInfo;
  // Vend sale state. A cancelled/returned order with vendSaleId set and vendVoidedAt
  // null still has a LIVE sale in Vend — revenue and a stock decrement standing against
  // goods that were never delivered. Voiding is a manual action now, so the list flags
  // these until someone deals with them.
  vendSaleId?: string | null;
  vendVoidedAt?: string | null;
  // Loop's order.returned (confirmed with Loop): the courier brought the goods back to
  // the store — the case where voiding the sale (and so restoring stock) is right.
  returnedToStoreAt?: string | null;
  // Who ended a cancelled order: customer | courier (the driver's delivery failed) | deeliver.
  cancelledBy?: string | null;
}

export interface ThreatInfo {
  level: ThreatLevel;
  remainingMinutes: number;
  slaMinutes: number;
  stage: 'picking' | 'packing' | 'pickup';
}

export interface PriorityInfo {
  badges: PriorityBadge[];
  isVIP: boolean;
  score: number; // For sorting
}

export interface Insight {
  id: string;
  type: 'bottleneck' | 'driver-shortage' | 'store-imbalance';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  affectedOrderIds: string[];
  count: number;
}

export interface OrderStats {
  waitingToPack: number;
  packed: number;
  inTransit: number;
  deliveredToday: number;
  /** Today's deliveries that took over an hour from placed to delivered. */
  completedOverHourToday?: number;
}

export interface ThreatSummary {
  critical: number;
  warning: number;
  watch: number;
  clear: number;
}

interface HUDContextValue {
  // Computed data
  orderThreats: Map<string, ThreatInfo>;
  orderPriorities: Map<string, PriorityInfo>;
  insights: Insight[];
  stats: OrderStats;
  threatSummary: ThreatSummary;

  // Highlight state
  highlightedOrderIds: Set<string>;
  setHighlightedOrderIds: (ids: Set<string>) => void;

  // Actions
  computeAnalytics: (orders: Order[]) => void;

  // Sound visualization
  isSoundPlaying: boolean;
  triggerSoundAnimation: () => void;
}

const HUDContext = createContext<HUDContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface HUDProviderProps {
  children: React.ReactNode;
}

export function HUDProvider({ children }: HUDProviderProps): React.ReactElement {
  // Threat detection map
  const [orderThreats, setOrderThreats] = useState<Map<string, ThreatInfo>>(new Map());

  // Priority detection map
  const [orderPriorities, setOrderPriorities] = useState<Map<string, PriorityInfo>>(new Map());

  // Operational insights
  const [insights, setInsights] = useState<Insight[]>([]);

  // Server-computed stats (updated by data provider via orderAnalyticsStore)
  const stats = useSyncExternalStore(subscribeOrderStats, getOrderStats) as OrderStats;

  // Threat summary (recomputed on each tick for live SLA accuracy)
  const [threatSummary, setThreatSummary] = useState<ThreatSummary>({
    critical: 0,
    warning: 0,
    watch: 0,
    clear: 0,
  });

  // Highlighted orders (for insight click-to-highlight)
  const [highlightedOrderIds, setHighlightedOrderIds] = useState<Set<string>>(new Set());

  // Sound visualization
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  const soundTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const triggerSoundAnimation = useCallback(() => {
    setIsSoundPlaying(true);
    if (soundTimeoutRef.current) {
      clearTimeout(soundTimeoutRef.current);
    }
    soundTimeoutRef.current = setTimeout(() => {
      setIsSoundPlaying(false);
    }, 2000); // Animation duration
  }, []);

  /**
   * Process server-enriched orders.
   *
   * - Reads server-computed `threat.slaMinutes` and `threat.stage`, then
   *   recomputes `remainingMinutes` and `level` from current time for live
   *   SLA countdowns (the 30-second tick keeps these fresh).
   * - Reads server-computed `priority` directly (no client recomputation).
   * - Generates operational insights client-side (cross-order pattern detection).
   * - Stats come from the server via orderAnalyticsStore (cross-page accurate).
   */
  const computeAnalytics = useCallback((orders: Order[]) => {
    if (!orders || orders.length === 0) return;

    const now = Date.now();
    const newThreats = new Map<string, ThreatInfo>();
    const newPriorities = new Map<string, PriorityInfo>();
    const newThreatSummary: ThreatSummary = { critical: 0, warning: 0, watch: 0, clear: 0 };

    // Insight tracking
    const placedOrders: string[] = [];
    const packedWithoutDriver: string[] = [];
    const storeOrderCounts: Record<string, number> = {};

    orders.forEach((order) => {
      const status = order.status?.toLowerCase() || 'placed';

      // Skip completed/cancelled orders for threat/priority analysis
      if (['delivered', 'cancelled', 'returned'].includes(status)) {
        return;
      }

      // =======================================================================
      // Threat: use server's slaMinutes/stage, recompute remaining for live countdown
      // =======================================================================
      if (order.threat) {
        const createdAt = new Date(order.createdAt).getTime();
        const elapsedMinutes = (now - createdAt) / 60000;
        const remainingMinutes = order.threat.slaMinutes - elapsedMinutes;

        let level: ThreatLevel;
        if (remainingMinutes <= 0) level = 'critical';
        else if (remainingMinutes <= 3) level = 'warning';
        else if (remainingMinutes <= 5) level = 'watch';
        else level = 'clear';

        newThreats.set(order.id, {
          level,
          remainingMinutes: Math.max(0, remainingMinutes),
          slaMinutes: order.threat.slaMinutes,
          stage: order.threat.stage as 'picking' | 'packing' | 'pickup',
        });

        newThreatSummary[level]++;
      }

      // =======================================================================
      // Priority: read directly from server-computed fields
      // =======================================================================
      if (order.priority) {
        newPriorities.set(order.id, {
          badges: order.priority.badges as PriorityBadge[],
          isVIP: order.priority.isVIP,
          score: order.priority.score,
        });
      }

      // =======================================================================
      // Insight Tracking
      // =======================================================================
      if (status === 'placed') {
        placedOrders.push(order.id);
      }

      if (status === 'packed' && !order.driverName) {
        packedWithoutDriver.push(order.id);
      }

      if (order.storeId) {
        storeOrderCounts[order.storeId] = (storeOrderCounts[order.storeId] || 0) + 1;
      }
    });

    // =======================================================================
    // Generate Insights (client-side pattern recognition)
    // =======================================================================
    const newInsights: Insight[] = [];

    // Picking Bottleneck: 5+ orders stuck at "placed"
    if (placedOrders.length >= 5) {
      newInsights.push({
        id: 'picking-bottleneck',
        type: 'bottleneck',
        severity: placedOrders.length >= 10 ? 'critical' : 'warning',
        title: 'Picking Bottleneck',
        description: `${placedOrders.length} orders waiting to be picked`,
        affectedOrderIds: placedOrders,
        count: placedOrders.length,
      });
    }

    // Driver Shortage: 3+ packed orders without driver
    if (packedWithoutDriver.length >= 3) {
      newInsights.push({
        id: 'driver-shortage',
        type: 'driver-shortage',
        severity: packedWithoutDriver.length >= 5 ? 'critical' : 'warning',
        title: 'Driver Shortage',
        description: `${packedWithoutDriver.length} packed orders awaiting driver`,
        affectedOrderIds: packedWithoutDriver,
        count: packedWithoutDriver.length,
      });
    }

    // Store Imbalance: One store 2x busier than average
    const storeIds = Object.keys(storeOrderCounts);
    if (storeIds.length > 1) {
      const avgOrders = storeIds.reduce((sum, id) => sum + storeOrderCounts[id], 0) / storeIds.length;
      storeIds.forEach((storeId) => {
        if (storeOrderCounts[storeId] >= avgOrders * 2 && storeOrderCounts[storeId] >= 5) {
          const storeOrderIds = orders
            .filter((o) => o.storeId === storeId && !['delivered', 'cancelled', 'returned'].includes(o.status?.toLowerCase() || ''))
            .map((o) => o.id);

          newInsights.push({
            id: `store-imbalance-${storeId}`,
            type: 'store-imbalance',
            severity: 'info',
            title: 'Store Imbalance',
            description: `Store has ${storeOrderCounts[storeId]} active orders (2x average)`,
            affectedOrderIds: storeOrderIds,
            count: storeOrderCounts[storeId],
          });
        }
      });
    }

    // Update state
    setOrderThreats(newThreats);
    setOrderPriorities(newPriorities);
    setInsights(newInsights);
    setThreatSummary(newThreatSummary);
  }, []);

  // Cleanup sound timeout on unmount
  useEffect(() => {
    return () => {
      if (soundTimeoutRef.current) {
        clearTimeout(soundTimeoutRef.current);
      }
    };
  }, []);

  const contextValue = useMemo<HUDContextValue>(
    () => ({
      orderThreats,
      orderPriorities,
      insights,
      stats,
      threatSummary,
      highlightedOrderIds,
      setHighlightedOrderIds,
      computeAnalytics,
      isSoundPlaying,
      triggerSoundAnimation,
    }),
    [
      orderThreats,
      orderPriorities,
      insights,
      stats,
      threatSummary,
      highlightedOrderIds,
      computeAnalytics,
      isSoundPlaying,
      triggerSoundAnimation,
    ]
  );

  return <HUDContext.Provider value={contextValue}>{children}</HUDContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useHUD(): HUDContextValue {
  const context = useContext(HUDContext);
  if (context === undefined) {
    throw new Error('useHUD must be used within a HUDProvider');
  }
  return context;
}

export default HUDContext;
